import { v4 as uuidv4 } from 'uuid';

export interface BookingKeyRef {
  current: { fingerprint: string; key: string } | null;
}

/** One Idempotency-Key per booking attempt: it changes only when the request body does.
 *  `confirmDuplicate` is excluded because the backend's request hash ignores it too. */
export const idempotencyKeyFor = (
  ref: BookingKeyRef,
  payload: object
): string => {
  const fingerprint = JSON.stringify({
    ...payload,
    confirmDuplicate: undefined,
  });
  if (ref.current?.fingerprint !== fingerprint) {
    ref.current = { fingerprint, key: uuidv4() };
  }
  return ref.current.key;
};

export type BookingFailure =
  | { kind: 'duplicate'; masterTrackingNumber?: string }
  | { kind: 'unknown_status' }
  | { kind: 'timeout' }
  | { kind: 'in_progress' }
  | { kind: 'key_reused' }
  | { kind: 'other' };

const CODES = {
  DUPLICATE_BOOKING: 'duplicate',
  BOOKING_STATUS_UNKNOWN: 'unknown_status',
  BOOKING_IN_PROGRESS: 'in_progress',
  IDEMPOTENCY_KEY_REUSED: 'key_reused',
} as const;

const GATEWAY_STATUSES = [502, 503, 504];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value != null && typeof value === 'object' && !Array.isArray(value);

/** The error body, its `data` envelope and the nested payload, as `return_class` nests them. */
const bodyLayers = (err: unknown): Record<string, unknown>[] => {
  const e = err as { response?: { data?: unknown }; data?: unknown };
  const root = isRecord(e?.response?.data)
    ? e.response.data
    : isRecord(e?.data)
      ? e.data
      : undefined;
  if (!root) return [];
  const envelope = isRecord(root.data) ? root.data : undefined;
  const payload = isRecord(envelope?.data) ? envelope.data : undefined;
  return [payload, envelope, root].filter(isRecord);
};

export const classifyBookingError = (err: unknown): BookingFailure => {
  const layers = bodyLayers(err);
  for (const layer of layers) {
    const kind = CODES[layer.code as keyof typeof CODES];
    if (!kind) continue;
    if (kind !== 'duplicate') return { kind };
    const master = layers
      .map((l) => l.masterTrackingNumber)
      .find((v): v is string => typeof v === 'string' && v !== '');
    return { kind, masterTrackingNumber: master };
  }
  const e = err as {
    isAxiosError?: boolean;
    response?: unknown;
    code?: string;
  };
  // An axios failure with no response (never a blocked preview write, which is a plain Error).
  const noResponse =
    e?.isAxiosError === true && !e.response && e.code !== 'ERR_CANCELED';
  const status = (err as { status?: number } | undefined)?.status;
  if (noResponse || GATEWAY_STATUSES.includes(status ?? 0)) {
    return { kind: 'timeout' };
  }
  return { kind: 'other' };
};

export type BookingAttempt<T> =
  | { outcome: 'booked'; data: T }
  | { outcome: 'failed'; failure: BookingFailure; error: unknown };

/** Sends one booking under its idempotency key and classifies a failure. */
export const attemptBooking = async <T>(
  ref: BookingKeyRef,
  payload: object,
  send: (idempotencyKey: string) => Promise<T>
): Promise<BookingAttempt<T>> => {
  const idempotencyKey = idempotencyKeyFor(ref, payload);
  try {
    return { outcome: 'booked', data: await send(idempotencyKey) };
  } catch (error) {
    const failure = classifyBookingError(error);
    if (failure.kind === 'key_reused') ref.current = null;
    return { outcome: 'failed', failure, error };
  }
};

/** Kinds where booking again is possible only after the user confirms. */
export const needsConfirmation = (f: BookingFailure): boolean =>
  f.kind === 'duplicate' || f.kind === 'unknown_status';

export const bookingFailureMessage = (
  f: BookingFailure,
  providerLabel: string
): string | undefined => {
  switch (f.kind) {
    case 'duplicate':
      return `This looks like a duplicate of an earlier booking${
        f.masterTrackingNumber
          ? ` (master tracking number ${f.masterTrackingNumber})`
          : ''
      }. Book again only if you want a second shipment.`;
    case 'unknown_status':
    case 'timeout':
      return `Booking status unknown, check ${providerLabel} before rebooking.`;
    case 'in_progress':
      return 'This booking is already in progress. Wait a moment and check before booking again.';
    case 'key_reused':
      return 'The booking request changed after it was first sent. Book again to send the current one.';
    case 'other':
      return undefined;
  }
};
